import { FilterAlt } from "@mui/icons-material";
import { Box, Checkbox, ListItemText, MenuItem, MenuList, TextField } from "@mui/material";
import { type Dispatch, type SetStateAction, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalManualLocationOrder } from "../../contexts/Staffing/GlobalManualLocationOrderContext";
import { compareValues } from "../../utils/multiColumnSorting";
import { ToolbarTriggerButton } from "../UIToolkit/Clickable/ToolbarTriggerButton";
import { SystemReactTinyPopover, SystemReactTinyPopoverRootStyle } from "../UIToolkit/Popups/SystemReactTinyPopover";

type DayViewLocationFilterProps = {
	selectedLocations: string[];
	setSelectedLocations: Dispatch<SetStateAction<string[]>>;
	availableLocations: string[];
};

export const DayViewLocationFilter = ({ selectedLocations, setSelectedLocations, availableLocations }: DayViewLocationFilterProps) => {
	const { t } = useTranslation();
	const [locationSearch, setLocationSearch] = useState<string>("");
	// A boolean, not an anchor element: react-tiny-popover positions against its own child, so the
	// element was only ever read as "is it open" — and holding it that way hid that the trigger could
	// not close. It sits inside the popover's wrapper, so clicking it is never an outside click.
	const [isLocationFilterOpen, setIsLocationFilterOpen] = useState(false);
	const { customStringOrderMap } = useGlobalManualLocationOrder();

	const sortedLocations = useMemo<string[]>(
		() => [...availableLocations].sort((a, b) => compareValues(a, b, customStringOrderMap)),
		[availableLocations, customStringOrderMap],
	);

	const filteredLocations = useMemo<string[]>(
		() => sortedLocations.filter((locationName) => locationName.toLowerCase().includes(locationSearch.toLowerCase().trim())),
		[sortedLocations, locationSearch],
	);

	// Filter is "active" (some hidden) only when a strict subset is selected.
	// `selectedLocations` empty OR fully populated both mean "show all".
	const isFilterActive = selectedLocations.length > 0 && selectedLocations.length < availableLocations.length;

	const handleLocationToggle = (locationName: string): void => {
		setSelectedLocations((prevSelected) =>
			prevSelected.includes(locationName) ? prevSelected.filter((location) => location !== locationName) : [...prevSelected, locationName],
		);
	};

	const handleSelectAllLocationsToggle = (): void => {
		setSelectedLocations((prevSelected) => (prevSelected.length === availableLocations.length ? [] : availableLocations));
	};

	return (
		<SystemReactTinyPopover
			arrowOpacity={1}
			isOpen={isLocationFilterOpen}
			onClose={() => setIsLocationFilterOpen(false)}
			positions={["right"]}
			content={
				<Box>
					<Box sx={{ px: 1, pb: 1 }}>
						<TextField
							fullWidth
							size="small"
							variant="outlined"
							placeholder="Search location"
							value={locationSearch}
							onChange={(e) => setLocationSearch(e.target.value)}
						/>
					</Box>
					<MenuList sx={{ p: 0 }}>
						<MenuItem onClick={handleSelectAllLocationsToggle}>
							<Checkbox
								size="small"
								checked={selectedLocations.length === availableLocations.length && availableLocations.length > 0}
								indeterminate={selectedLocations.length > 0 && selectedLocations.length < availableLocations.length}
							/>
							<ListItemText primary="All locations" />
						</MenuItem>
						{filteredLocations.filter(Boolean).map((locationName) => (
							<MenuItem key={locationName} onClick={() => handleLocationToggle(locationName)}>
								<Checkbox size="small" checked={selectedLocations.includes(locationName)} />
								<ListItemText primary={locationName} />
							</MenuItem>
						))}
					</MenuList>
				</Box>
			}
			contentRootStyle={{
				...SystemReactTinyPopoverRootStyle,
				maxHeight: 320,
				overflowY: "auto",
			}}
		>
			<ToolbarTriggerButton
				label={t("location.filter.label")}
				description={isFilterActive ? t("location.filter.active.tooltip", { shown: selectedLocations.length, total: availableLocations.length }) : undefined}
				icon={<FilterAlt />}
				// The count answers "how many am I hiding behind this?", so it only means something while a
				// strict subset is picked — the dot badge it replaces was on the same condition.
				count={isFilterActive ? selectedLocations.length : undefined}
				onClick={() => setIsLocationFilterOpen((prev) => !prev)}
				dataTestId="day-view-location-filter-button"
			/>
		</SystemReactTinyPopover>
	);
};
